// Display-only Indonesian labels for strings that must stay ENGLISH internally
// (they are stored in the profile, sent to the backend, or matched by the scoring
// engine / frontend enums). We keep the value as-is and only translate what the
// user SEES. Use idLabel(value) at render time; it falls back to the value itself.
//
// Do NOT change the KEYS here — they are the canonical stored/compared values.
const ID_LABELS = {
  // ── Onboarding: environments ──
  "Office district": "Kawasan perkantoran",
  "Residential area": "Kawasan pemukiman",
  "School / university area": "Kawasan sekolah / kampus",
  "Shopping district": "Kawasan perbelanjaan",
  "Traditional market / Pasar": "Pasar tradisional",
  "Transit hub / Station area": "Simpul transit / area stasiun",
  "Tourism area": "Kawasan wisata",
  "Hospital / healthcare cluster": "Klaster rumah sakit / kesehatan",
  "Industrial area": "Kawasan industri",
  "Highway / main road corridor": "Koridor jalan utama / raya",

  // ── Onboarding: avoid conditions ──
  "Low purchasing power": "Daya beli rendah",
  "Very low foot traffic": "Lalu lalang sangat sepi",
  "Far from public transit": "Jauh dari transportasi umum",
  "Low parking availability": "Lahan parkir minim",
  "Poor road / street access": "Akses jalan buruk",
  "Hard truck/logistics access": "Akses truk/logistik sulit",

  // ── Onboarding: priorities ──
  "High foot traffic": "Lalu lalang ramai",
  "High street visibility": "Visibilitas jalan tinggi",
  "High purchasing power": "Daya beli tinggi",
  "Low competition": "Kompetisi rendah",
  "Easy access & parking": "Akses & parkir mudah",
  "Near anchor tenants": "Dekat tenant besar",
  "Delivery-friendly coverage": "Cakupan ramah pengiriman",

  // ── Onboarding: primary customers ──
  "Students": "Pelajar",
  "Office workers": "Pekerja kantoran",
  "Young adults / Gen Z": "Dewasa muda / Gen Z",
  "Families": "Keluarga",
  "Stay-at-home parents": "Orang tua di rumah",
  "Senior citizens": "Lansia",
  "Tourists": "Wisatawan",
  "Commuters / transit users": "Komuter / pengguna transit",
  "MSMEs / business owners": "UMKM / pemilik usaha",
  "High-income professionals": "Profesional berpenghasilan tinggi",

  // ── Onboarding: busy times ──
  "Morning rush (6–10am)": "Sibuk pagi (06–10)",
  "Lunch hours (11am–2pm)": "Jam makan siang (11–14)",
  "Afternoon (2–5pm)": "Siang–sore (14–17)",
  "Evening / dinner (5–9pm)": "Malam / makan malam (17–21)",
  "Late night (9pm+)": "Larut malam (21+)",
  "Weekend-focused": "Fokus akhir pekan",
  "All-day / consistent": "Seharian / konsisten",

  // ── Onboarding: rental budget bands ──
  "Varies by location": "Bervariasi per lokasi",
  "Under 5M": "Di bawah 5jt",
  "5–15M": "5–15jt",
  "15–30M": "15–30jt",
  "30–100M": "30–100jt",

  // ── Onboarding: POI options ──
  "Bus stop / Halte": "Halte bus",
  "Train / MRT / Bus terminal": "Stasiun / MRT / terminal bus",
  "School / Kindergarten": "Sekolah / TK",
  "University / Campus": "Universitas / kampus",
  "Hospital / Clinic / Pharmacy": "RS / klinik / apotek",
  "Restaurant / Cafe / F&B": "Restoran / kafe / F&B",
  "Traditional Market": "Pasar tradisional",
  "Mall / Supermarket": "Mal / supermarket",
  "Minimarket / Retail shops": "Minimarket / toko ritel",
  "Salon / Barbershop": "Salon / pangkas rambut",
  "Laundry / Daily services": "Laundry / jasa harian",
  "Bank / ATM": "Bank / ATM",
  "Gas Station": "SPBU",
  "Office / Co-working": "Kantor / co-working",
  "Apartment / Housing complex": "Apartemen / perumahan",
  "Cinema / Karaoke": "Bioskop / karaoke",
  "Gym / Sports center": "Gym / pusat olahraga",
  "Hotel": "Hotel",
  "Park / Green space": "Taman / ruang hijau",
  "Place of worship": "Tempat ibadah",
  "Government / Post office": "Kantor pemerintah / pos",

  // ── Onboarding: market/income tiers ──
  "Low income": "Berpenghasilan rendah",
  "Lower-middle income": "Menengah bawah",
  "Middle income": "Menengah",
  "Upper-middle income": "Menengah atas",
  "High income": "Berpenghasilan tinggi",

  // ── Benchmark / reference types ──
  "Direct Competitor": "Kompetitor Langsung",
  "Indirect Competitor": "Kompetitor Tidak Langsung",
  "Aspirational": "Aspirasional",
  "Strategic": "Strategis",
  "Complementary": "Komplementer",
  "Preference": "Preferensi",

  // ── Site-score suitability tiers ──
  "Low": "Rendah", "Fair": "Cukup", "Moderate": "Sedang",
  "Good": "Bagus", "High": "Tinggi", "Best": "Terbaik",

  // ── Profile-fit group names (backend keeps these English for matching) ──
  "Budget vs Rent": "Anggaran vs Sewa",
  "Purchasing Power": "Daya Beli",
  "POI Priorities": "Prioritas POI",
  "Desired Environment": "Lingkungan Diinginkan",
  "Avoid Conditions": "Kondisi Dihindari",
  "Target Customers": "Pelanggan Target",
  "Audience Demographics": "Demografi Audiens",
  "Activity Timing": "Waktu Aktivitas",
  "Priorities": "Prioritas",

  // ── On-ground verdict labels (Gemini) ──
  "Confirms": "Menguatkan",
  "Adds Caution": "Perlu Kehati-hatian",
  "Contradicts": "Bertentangan",

  // ── Competitor type + threat + intensity + price position ──
  "Direct": "Langsung", "Indirect": "Tidak Langsung",
  "Medium": "Sedang",
  "low": "rendah", "medium": "sedang", "high": "tinggi",
  "below": "di bawah", "at": "setara", "above": "di atas",

  // ── Ecosystem category keys that stayed English (backend place_context.ECOSYSTEM_SPEC) ──
  "Office": "Perkantoran",

  // ── Rent class (Gemini rent_estimate.market_position) ──
  "Budget Tier": "Kelas Hemat", "Mid Tier": "Kelas Menengah", "Premium Tier": "Kelas Premium",

  // ── Competitor price bucket (market.price_positioning.modal) ──
  "budget": "Hemat", "mid": "Menengah", "premium": "Premium",

  // ── Stored target segment (profile.target_ses) ──
  "low_income": "berpenghasilan rendah", "low_middle": "menengah bawah",
  "middle_income": "menengah", "upper_income": "menengah atas", "all": "semua segmen",

  // ── Purchase frequency units (profile.buyingFrequencyUnit) ──
  "per week": "per minggu", "per month": "per bulan", "per year": "per tahun",
};

// Return the Indonesian display label for a canonical value, or the value itself.
export function idLabel(v) {
  if (v == null) return v;
  return ID_LABELS[v] ?? v;
}

export default ID_LABELS;
